import React from "react";
import {
    MagnifyingGlassIcon,
    ArrowLeftIcon,
} from "@heroicons/react/24/outline";

import useManageSubscription from "./hooks/useManageSubscription";
import MemberInformation from "./components/MemberInformation";
import SubscriptionTable from "./components/SubscriptionTable";
import SubscriptionDetails from "./components/SubscriptionDetails";
import CartSummary from "./components/CartSummary";
import RefundModal from "./components/RefundModal";

export default function MemberSubscription() {
    const {
        loadingMemberInfo,
        memberInfoError,
        isRefundModalOpen,
        setIsRefundModalOpen,
        refunding,
        refundSuccessMsg,
        paymentCollecting,
        paymentSuccessMsg,
        paymentMethod,
        setPaymentMethod,
        paymentAmount,
        setPaymentAmount,
        subscriptions,
        loadingSubs,
        subsError,
        selectedSubscriptionId,
        subscriptionDetails,
        loadingSubDetails,
        subDetailsError,
        query,
        setQuery,
        cart,
        loadingProducts,
        productsError,
        handleGoBack,
        addToCart,
        removeFromCart,
        handleViewSubscription,
        handleRequestRefund,
        handleProcessRefund,
        handleCollectPayment,
        filteredProducts,
        memberData,
        depositAmount,
        overdueCharges,
        damageCharges,
        finalRefund,
        requestingRefund,
        toast,
        showRefundButton,
    } = useManageSubscription();

    return (
        <div className="flex h-screen w-full bg-white font-sans text-gray-700">
            {/* Toast Notification */}
            {toast && (
                <div className={`fixed top-5 right-5 z-[9999] px-4 py-3 rounded-lg shadow-xl flex items-center gap-2 border text-sm font-semibold transition-all duration-300 ${
                    toast.type === "success" 
                        ? "bg-green-50 border-green-200 text-green-800" 
                        : "bg-red-50 border-red-200 text-red-800"
                }`}>
                    <span>{toast.type === "success" ? "✅" : "⚠️"}</span>
                    <span>{toast.message}</span>
                </div>
            )}
            {/* LEFT SIDE (Plans) */}
            <div className="w-[30%] border-r border-gray-200 p-6 flex flex-col h-screen bg-gray-50/50">
                {/* Search */}
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>

                    <input
                        type="text"
                        placeholder="Search Subscription"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-400 focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-200 sm:text-sm"
                    />
                </div>

                {/* Plans Scroll Area */}
                <div className="mt-6 space-y-6 overflow-y-auto flex-1 pr-2">
                    {loadingProducts && (
                        <div className="text-xs text-gray-500">Loading plans...</div>
                    )}

                    {!loadingProducts && productsError && (
                        <div className="text-xs text-red-500">{productsError}</div>
                    )}

                    {!loadingProducts && !productsError && filteredProducts.map((p) => {
                        const billingInterval = p.billing_interval;
                        const billingPeriod = p.billing_period;
                        const interval = Number(billingInterval);
                        const period = String(billingPeriod).toLowerCase();
                        let durationLabel = "-";
                        if (billingInterval && billingPeriod) {
                            if (period === "month") {
                                durationLabel = interval === 1 ? "1 Month" : `${interval} Months`;
                            } else if (period === "year") {
                                durationLabel = interval === 1 ? "12 Months" : `${interval} Years`;
                            } else {
                                durationLabel = `${interval} ${period}`;
                            }
                        }

                        return (
                            <div
                                key={p.product_id}
                                className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-all flex gap-4 items-center justify-between"
                            >
                                {/* Left: Image and Details */}
                                <div className="flex items-center gap-3.5 min-w-0 flex-1">
                                    <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center">
                                        {p.image ? (
                                            <img
                                                src={p.image}
                                                alt={p.name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-600 font-bold text-sm">
                                                {p.name.charAt(0)}
                                            </div>
                                        )}
                                    </div>

                                    <div className="min-w-0 flex-1 space-y-0.5">
                                        <h4 className="font-semibold text-gray-900 text-sm leading-snug break-words">
                                            {p.name}
                                        </h4>
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                                            <span className="text-gray-500 font-medium">
                                                {durationLabel}
                                            </span>
                                            <span className="text-gray-300">•</span>
                                            <span className="font-semibold text-gray-900">
                                                ₹{p.price_formatted || p.price}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex-shrink-0">
                                    <button
                                        onClick={() => addToCart(p)}
                                        className="h-8 px-3.5 rounded-lg bg-black text-white hover:bg-gray-900 text-xs font-bold transition-all shadow-sm active:scale-95 flex items-center justify-center"
                                        title="Add to cart"
                                    >
                                        Add Plan
                                    </button>
                                </div>
                            </div>
                        );
                    })}

                    {!loadingProducts && !productsError && filteredProducts.length === 0 && (
                        <div className="text-xs text-gray-400 text-center py-8">
                            No plans found
                        </div>
                    )}
                </div>
            </div>

            {/* RIGHT SIDE */}
            <div className="w-[70%] p-6 overflow-y-auto relative">
                {/* Back Header */}
                <div className="flex items-center gap-3 mb-6">
                    <button
                        onClick={handleGoBack}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 transition-all shadow-sm shrink-0"
                        title="Go Back"
                    >
                        <ArrowLeftIcon className="h-4 w-4" />
                        <h2 className="font-semibold text-gray-900 text-sm tracking-tight whitespace-nowrap">
                            Go Back
                        </h2>
                    </button>
                </div>

                {/* Close Button */}
                <div className="absolute top-6 right-6">
                    <button
                        onClick={handleGoBack}
                        className="border border-gray-300 rounded px-4 py-1.5 text-sm font-medium hover:bg-gray-50 text-black"
                    >
                        Close
                    </button>
                </div>

                <div className="mt-12">
                    {/* Member Information */}
                    <MemberInformation
                        memberData={memberData}
                        loadingSubs={loadingSubs}
                        loadingMemberInfo={loadingMemberInfo}
                        memberInfoError={memberInfoError}
                    />

                    {/* Subscription Table & Details */}
                    <SubscriptionTable
                        subscriptions={subscriptions}
                        loadingSubs={loadingSubs}
                        subsError={subsError}
                        onView={handleViewSubscription}
                    />

                    <SubscriptionDetails
                        selectedSubscriptionId={selectedSubscriptionId}
                        subscriptionDetails={subscriptionDetails}
                        loadingSubDetails={loadingSubDetails}
                        subDetailsError={subDetailsError}
                        onRequestRefund={handleRequestRefund}
                        requestingRefund={requestingRefund}
                        showRefundButton={showRefundButton}
                    />

                    {/* Cart Summary */}
                    <CartSummary
                        cart={cart}
                        onRemove={removeFromCart}
                    />
                </div>

                {/* Refund & Dues Settlement Modal */}
                {isRefundModalOpen && (
                    <RefundModal
                        onClose={() => setIsRefundModalOpen(false)}
                        memberData={memberData}
                        refundSuccessMsg={refundSuccessMsg}
                        paymentSuccessMsg={paymentSuccessMsg}
                        depositAmount={depositAmount}
                        overdueCharges={overdueCharges}
                        damageCharges={damageCharges}
                        finalRefund={finalRefund}
                        refunding={refunding}
                        onProcessRefund={handleProcessRefund}
                        paymentAmount={paymentAmount}
                        onChangePaymentAmount={setPaymentAmount}
                        paymentMethod={paymentMethod}
                        onChangePaymentMethod={setPaymentMethod}
                        paymentCollecting={paymentCollecting}
                        onCollectPayment={handleCollectPayment}
                    />
                )}
            </div>
        </div>
    );
}
